<template>
	<div class="card-settings-container">
		<ModalHeader :title="getString('card_settings')" :button-text="getString('save')" :button-loading="loadingSave" @click-button="saveChanges" />

		<!-- Test Mode Simulation Banner -->
		<div v-if="isTestMode" class="test-simulation-banner">
			<div class="test-warning">
				<span class="test-label">{{ getString('issuing_test_mode_only') }}</span>
			</div>
			<div class="test-simulate-form">
				<AppNumberInput
					double-decimal
					:symbol="currencyStore.currCurrency.symbol"
					:label="getString('issuing_simulate_authorization_amount')"
					:value="simulateAuthAmount"
					:placeholder="formatBalance(1000)"
					:min="1"
					:step="10"
					@changed-value="v => simulateAuthAmount = v"
				/>
				<AppButton
					outline
					:text="getString('issuing_simulate_authorization')"
					:loading="processingAuthSimulation"
					:disabled="!simulateAuthAmount || simulateAuthAmount <= 0"
					@click="handleSimulateAuthorization"
				/>
			</div>
		</div>

		<div class="card-settings">
			<AppList :items="items" >
				<template #card_pin="{item}">
					<div class="card-pin-container row space-between align-center">
						<div class="list-item-header">
							<p class="list-item-title">{{ item.title }}</p>
							<div class="pin-shower">
								<div class="pin-shower-value" id="card-pin" v-show="isShowingDetails"></div>
								<div class="pin-shower-value" id="card-pin" v-show="!isShowingDetails">• • • •</div>
								<AppButton :text="getString(isShowingDetails ? 'hide_pin' : 'show_pin')" :loading="loadingDetails" outline @click="showPin" />
							</div>
						</div>
						<div class="divider"></div>
						<div class="list-item-header">
							<p class="list-item-title">{{ getString('change_pin') }}</p>
							<AppTextInput 
								:placeholder="getString('new_pin')" 
								:value="newPin || ''" 
								@changed-text="(value) => newPin = value" 
								type="password" 
								maxlength="4"
							/>
						</div>
					</div>
				</template>
				<template #card_spending="{item}">
					<div class="card-spending-container">
						<AppLoader v-if="loadingSpendingControls" />
						<AppList v-else :items="spendingItems">
							<template #time_limits="{item}">
								<div class="spending-limits-section">
									<div class="spending-limit-item">
										<div class="limit-row">
											<AppNumberInput 
												:placeholder="getString('amount')" 
												:value="currencyStore.dbToActualValue(spendingLimit.amount)" 
												@changed-value="updateLimitAmount"
												:symbol="currencyStore.currCurrency.symbol"
											/>
											<AppSelect 
												:values="intervalValues" 
												:selected-index="selectedIntervalIndex"
												@selected="updateLimitInterval"
											/>
										</div>
									</div>
								</div>
							</template>
							<template #categories_limits="{item}">
								<div class="merchant-categories-section">
									<div class="categories-mode-selection">
										<div class="mode-select-item" :class="{ active: categoriesMode === 'allowed' }" @click="setCategoriesMode('allowed')">
											<p>{{ getString('allowed_categories') }}</p>
										</div>
										<div class="mode-select-item" :class="{ active: categoriesMode === 'blocked' }" @click="setCategoriesMode('blocked')">
											<p>{{ getString('blocked_categories') }}</p>
										</div>
									</div>
									
									<div class="categories-control" v-if="categoriesMode === 'allowed'">
										<div class="categories-summary">
											<span v-if="allowedCategories.length === 0">{{ getString('all_categories_allowed') }}</span>
											<span v-else>{{ allowedCategories.length }} {{ getString('categories_selected') }}</span>
										</div>
										<AppButton 
											:text="getString('select_categories')" 
											@click="openCategoriesSheet"
											outline
										/>
									</div>
									
									<div class="categories-control" v-if="categoriesMode === 'blocked'">
										<div class="categories-summary">
											<span v-if="blockedCategories.length === 0">{{ getString('no_categories_blocked') }}</span>
											<span v-else>{{ blockedCategories.length }} {{ getString('categories_blocked') }}</span>
										</div>
										<AppButton 
											:text="getString('select_categories')" 
											@click="openCategoriesSheet"
											outline
										/>
									</div>
								</div>
							</template>
						</AppList>
					</div>
				</template>
			</AppList>
		</div>
		
		<!-- Merchant Categories Bottom Sheet -->
		<AppBottomSheet
			@toggling-popup="togglingCategoriesSheet"
			:force-width="bottomSheetWidth"
			:active="showCategoriesSheet"
		>
			<MerchantCategoriesBottomSheet
				:selected-categories="currentCategories"
				:mode="categoriesMode"
				@apply="updateCategories"
			/>
		</AppBottomSheet>
		
		<OtpPopup ref="otpPopup" @new-otp="handleOtpSubmitted" />
		<OtpPopup ref="otpPopupCancel" @new-otp="handleCancelOtpSubmitted" />
		<AppPopup 
			ref="confirmPopup" 
			:title="getString('cancel_card')"
			:body-text="getString('cancel_card_confirm')"
			:buttons="cancelConfirmButtons"
		/>
	</div>
</template>

<script>
import {defineComponent} from 'vue'
import AppList from "@/shared/components/AppList.vue";
import {useLanguageStore, useNotificationStore, useSvgStore, useCurrencyStore} from "@/shared/stores";
import {useCardsStore} from "@/console-new/stores/cards.store";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import {useUserStore} from "@/user/stores";
import API from '@/shared/helpers/API.js';
import AppButton from "@/shared/components/AppButton.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import MerchantCategoriesBottomSheet from "./MerchantCategoriesBottomSheet.vue";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import OtpPopup from "@/console-new/components/shared/OtpPopup.vue";
import AppPopup from "@/shared/components/AppPopup.vue";

export default defineComponent({
	setup() {
		const svgStore = useSvgStore();
		const cardsStore = useCardsStore();
		const languageStore = useLanguageStore();
		const notificationStore = useNotificationStore();
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		return {
			getIcon: svgStore.get,
			cardsStore,
			userStore,
			notificationStore,
			currencyStore,
			getString: languageStore.getString,
		}
	},
	mixins: [WindowWidth],
	name: "CardSettings",
	components: {AppTextInput, AppNumberInput, AppBottomSheet, ModalHeader, AppList, AppButton, AppSelect, AppLoader, MerchantCategoriesBottomSheet, OtpPopup, AppPopup},
	emits: ['toggling-popup'],
	props: {
		cardId: {
			type: [String, Number],
			default: null
		}
	},
	data() {
		return {
			loadingSave: false,
			loadingDetails: false,
			loadingSpendingControls: false,
			loadingPinUpdate: false,
			togglePin: false,
			toggleSpending: false,
			pinElement: null,
			newPin: null,
			// Spending limits data
			spendingLimit: {
				amount: 0,
				interval: 'daily'
			},
			allowedCategories: [],
			blockedCategories: [],
			// Categories management
			categoriesMode: 'allowed', // 'allowed' or 'blocked'
			showCategoriesSheet: false,
			// Spending sub-items toggles
			toggleTimeLimits: false,
			toggleCategoriesLimits: false,
			// Original spending controls for change detection
			originalSpendingControls: null,
			// For OTP verification
			pendingCardForPin: null,
			// Test simulation data
			simulateAuthAmount: null,
			processingAuthSimulation: false,
			// For card cancellation
			loadingCancel: false,
			showCancelConfirm: false
		}
	},
	computed: {
		spendingItems() {
			return [
				{
					title: this.getString('categories_limits'),
					switch: true,
					active: this.toggleCategoriesLimits,
					callback: (value) => {
						this.toggleCategoriesLimits = value;
					},
					dropdownSlot: 'categories_limits',
				},
				{
					title: this.getString('time_limits'),
					switch: true,
					active: this.toggleTimeLimits,
					callback: (value) => {
						this.toggleTimeLimits = value;
					},
					dropdownSlot: 'time_limits',
				},
			];
		},
		items() {
			return [
				{
					title: this.getString("card_name"),
					placeholder: this.getString("card_name"),
					textInput: true,
					value: this.currentCard ? this.currentCard.name : '',
					callbackText: (value) => {
						this.currentCard.name = value;
					},
				},
				{
					title: this.getString("card_pin"),
					chevronItem: true,
					active: this.togglePin,
					callback: (value) => {
						this.togglePin = value;
						this.toggleSpending = false;
					},
					dropdownSlot: 'card_pin',
				},
				{
					title: this.getString("card_spending"),
					chevronItem: true,
					active: this.toggleSpending,
					callback: (value) => {
						this.toggleSpending = value;
						this.togglePin = false;
						if (value && !this.loadingSpendingControls) {
							this.fetchCardSpendingControls();
						}
					},
					dropdownSlot: 'card_spending',
				},
				{
					title: this.getString("cancel_card"),
					buttonTitle: this.getString("cancel_card"),
					buttonLoading: this.loadingCancel,
					callbackButton: () => {
						this.handleCancelCard();
					},
					backgroundColor: 'var(--color-error-bg)',
				},
			]
		},
		currentCard() {
			return this.cardsStore.getCardById(this.cardId) || {};
		},
		isShowingDetails() {
			return this.cardsStore.stripe.detailsCardId === this.cardId;
		},
		intervalOptions() {
			return [
				{ value: 'per_authorization', label: this.getString('per_authorization') },
				{ value: 'daily', label: this.getString('daily') },
				{ value: 'weekly', label: this.getString('weekly') },
				{ value: 'monthly', label: this.getString('monthly') },
				{ value: 'yearly', label: this.getString('yearly') },
				{ value: 'all_time', label: this.getString('all_time') },
			];
		},
		intervalValues() {
			return this.intervalOptions.map(option => option.label);
		},
		selectedIntervalIndex() {
			const index = this.intervalOptions.findIndex(option => option.value === this.spendingLimit.interval);
			return index >= 0 ? index : 1; // Default to 'daily' (index 1)
		},
		currentCategories() {
			return this.categoriesMode === 'allowed' ? this.allowedCategories : this.blockedCategories;
		},
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else {
				return "960px";
			}
		},
		isValidPin() {
			if (!this.newPin) return false;
			const pin = this.newPin.toString();
			return /^\d{4}$/.test(pin);
		},
		isTestMode() {
			// Check if running in test mode
			return TEST;
		},
		cancelConfirmButtons() {
			return [
				{
					text: this.getString('cancel'),
					negative: true,
					callback: () => {
						this.$refs.confirmPopup.toggleStatus(false);
					}
				},
				{
					text: this.getString('confirm'),
					callback: () => {
						this.$refs.confirmPopup.toggleStatus(false);
						// After confirmation, ask for OTP
						this.$refs.otpPopupCancel.askOtp();
					}
				}
			];
		}
	},
	methods: {
		// Spending limits methods
		fetchCardSpendingControls() {
			this.loadingSpendingControls = true;
			API.init().getCardSpendingControls(this.userStore.currBusinessId, this.cardId, (response) => {
				this.loadingSpendingControls = false;
				if (response && response.result === "OK" && response.spending_controls) {
					this.updateSpendingControlsFromStripe(response.spending_controls);
				} else {
					this.notificationStore.showNotification("generic-fail");
				}
			});
		},
		updateSpendingControlsFromStripe(spendingControls) {
			// Store original data for change detection
			this.originalSpendingControls = JSON.parse(JSON.stringify(spendingControls));
			
			// Update spending limits (take first one if multiple exist)
			if (spendingControls.spending_limits && spendingControls.spending_limits.length > 0) {
				const firstLimit = spendingControls.spending_limits[0];
				this.spendingLimit = {
					amount: firstLimit.amount || 0,
					interval: firstLimit.interval || 'daily'
				};
				this.toggleTimeLimits = true;
			} else {
				this.spendingLimit = { amount: 0, interval: 'daily' };
				this.toggleTimeLimits = false;
			}
			
			// Update allowed/blocked categories
			this.allowedCategories = spendingControls.allowed_categories || [];
			this.blockedCategories = spendingControls.blocked_categories || [];
			
			// Set categories mode based on which categories exist
			if (this.blockedCategories.length > 0) {
				this.categoriesMode = 'blocked';
				this.toggleCategoriesLimits = true;
			} else if (this.allowedCategories.length > 0) {
				this.categoriesMode = 'allowed';
				this.toggleCategoriesLimits = true;
			} else {
				this.categoriesMode = 'allowed';
				this.toggleCategoriesLimits = false;
			}
		},
		updateLimitAmount(value) {
			this.spendingLimit.amount = this.currencyStore.actualToDbValue(value);
		},
		updateLimitInterval(selectedIndex) {
			const selectedOption = this.intervalOptions[selectedIndex];
			if (selectedOption) {
				this.spendingLimit.interval = selectedOption.value;
			}
		},
		setCategoriesMode(mode) {
			this.categoriesMode = mode;
		},
		openCategoriesSheet() {
			this.showCategoriesSheet = true;
		},
		togglingCategoriesSheet(active) {
			if(!active) {
				this.showCategoriesSheet = false;
			}
		},
		updateCategories(categories) {
			if (this.categoriesMode === 'allowed') {
				this.allowedCategories = categories;
			} else {
				this.blockedCategories = categories;
			}
			this.showCategoriesSheet = false;
		},
		hasSpendingControlsChanged() {
			if (!this.originalSpendingControls) {
				// If no original data, check if any limits are set
				return this.toggleTimeLimits || this.toggleCategoriesLimits;
			}
			
			// Build current spending controls
			const currentControls = {};
			
			// Check spending limits
			if (this.toggleTimeLimits && this.spendingLimit.amount > 0) {
				currentControls.spending_limits = [{
					amount: this.spendingLimit.amount,
					interval: this.spendingLimit.interval
				}];
			}
			
			// Check categories
			if (this.toggleCategoriesLimits) {
				if (this.categoriesMode === 'allowed' && this.allowedCategories.length > 0) {
					currentControls.allowed_categories = [...this.allowedCategories];
				} else if (this.categoriesMode === 'blocked' && this.blockedCategories.length > 0) {
					currentControls.blocked_categories = [...this.blockedCategories];
				}
			}
			
			// Compare with original
			return JSON.stringify(currentControls) !== JSON.stringify(this.originalSpendingControls);
		},
		saveChanges() {
			if (this.newPin && !this.isValidPin) {
				this.notificationStore.showNotification("invalid-pin-format");
				return;
			}
			
			this.loadingSave = true;
			
			const updateData = {
				name: this.currentCard.name,
			};
			
			// Only include spending controls if they've changed
			if (this.hasSpendingControlsChanged()) {
				const spendingControls = {};
				
				// Add or clear spending limits based on toggle state
				if (this.toggleTimeLimits && this.spendingLimit.amount > 0) {
					spendingControls.spending_limits = [{
						amount: this.spendingLimit.amount,
						interval: this.spendingLimit.interval
					}];
				} else {
					spendingControls.spending_limits = [];
				}
				
				// Add or clear category restrictions based on toggle state
				if (this.toggleCategoriesLimits) {
					if (this.categoriesMode === 'allowed' && this.allowedCategories.length > 0) {
						spendingControls.allowed_categories = this.allowedCategories;
						spendingControls.blocked_categories = [];
					} else if (this.categoriesMode === 'blocked' && this.blockedCategories.length > 0) {
						spendingControls.blocked_categories = this.blockedCategories;
						spendingControls.allowed_categories = [];
					} else {
						spendingControls.allowed_categories = [];
						spendingControls.blocked_categories = [];
					}
				} else {
					spendingControls.allowed_categories = [];
					spendingControls.blocked_categories = [];
				}
				
				updateData.spending_controls = JSON.stringify(spendingControls);
			}
			
			// Handle PIN encryption if provided
			if (this.newPin && this.isValidPin) {
				this.encryptPinAndSubmit(updateData);
				return;
			}
			
			this.submitCardUpdate(updateData);
		},
		encryptPinAndSubmit(updateData) {
			this.encryptPin(this.newPin, (encryptedPin) => {
				if (encryptedPin) {
					updateData.new_pin = encryptedPin;
					this.submitCardUpdate(updateData);
				} else {
					this.notificationStore.showNotification("generic-fail");
					this.loadingSave = false;
				}
			});
		},
		async encryptPin(pin, callback) {
			// Check if Web Crypto API is available
			if (!window.crypto || !window.crypto.subtle) {
				if (location.protocol === 'http:') {

					if (TEST) {
						console.error('PIN encryption requires HTTPS or trusted origins for testing.');
						console.info('For Chrome testing: Go to chrome://flags/#unsafely-treat-insecure-origin-as-secure');
						console.info('Add your domain (e.g., http://your-domain.local) to "Insecure origins treated as secure"');
						console.info('Alternatively, use https:// or localhost');
					} else {
						console.error('PIN encryption requires HTTPS. Please use https://');
					}
					
					this.notificationStore.showNotification("generic-fail");
					callback(null);
					return;
				}
				console.error('Web Crypto API not available');
				callback(null);
				return;
			}
			
			try {
				// Validate PIN format
				if (!/^\d{4}$/.test(pin)) {
					console.error('PIN must be exactly 4 numeric digits');
					callback(null);
					return;
				}

				// Dynamic import of JOSE library
				const { importJWK, CompactEncrypt } = await import('https://cdn.skypack.dev/jose@6.0.11');

				// Fetch Stripe's RSA public key directly
				const keyResponse = await fetch('https://issuing-key.stripe.com/v1/keys');
				const keyData = await keyResponse.json();

				if (!keyData.jwk || !keyData.key_id) {
					console.error('Failed to fetch Stripe encryption key');
					callback(null);
					return;
				}

				// Import the JWK public key
				const publicKey = await importJWK(keyData.jwk, 'RSA-OAEP');

				// Encrypt the PIN using RSA-OAEP and A128CBC-HS256 as per Stripe docs
				const jwe = await new CompactEncrypt(new TextEncoder().encode(pin))
					.setProtectedHeader({ alg: 'RSA-OAEP', enc: 'A128CBC-HS256', kid: keyData.key_id })
					.encrypt(publicKey);

				console.log('PIN encrypted successfully. Length:', jwe.length, 'Parts:', jwe.split('.').length);
				callback(jwe);

			} catch (error) {
				console.error('PIN encryption error:', error);
				callback(null);
			}
		},
		submitCardUpdate(updateData) {
			API.init().updateCard(this.userStore.currBusinessId, this.cardId, updateData, (response) => {
				this.loadingSave = false;
				if(response && response.result === "OK") {
					this.notificationStore.showNotification("generic-success");
					this.newPin = null; // Clear PIN field on success
					this.toggleSpending = false;
					this.$emit('toggling-popup', false);
					// Trigger refresh of chart and table data after 2s timeout to wait for webhook
					setTimeout(() => {
						this.cardsStore.triggerRefresh();
					}, 2000);
				} else {
					this.notificationStore.showNotification("generic-fail");
				}
			});
		},
		showPin() {
			if(this.cardsStore.stripe.detailsCardId) {
				this.cardsStore.hideCardDetails();
			} else {
				// Store the card data and show OTP popup
				this.pendingCardForPin = this.currentCard;
				this.$refs.otpPopup.askOtp();
			}
		},
		handleOtpSubmitted(otpCode) {
			if (!this.pendingCardForPin) {
				return;
			}
			
			this.loadingDetails = true;
			this.cardsStore.getStripeSecretKey(this.pendingCardForPin, otpCode, (key, error) => {
				this.loadingDetails = false;
				if(!key) {
					if (error && error.includes('invalid_otp')) {
						this.notificationStore.showNotification("invalid_otp");
					} else {
						this.notificationStore.showNotification("generic-fail");
					}
				}
				this.pendingCardForPin = null;
			});
		},
		formatBalance(amountInCents) {
			return this.currencyStore.readableCurrencyValue(amountInCents, true, true, false);
		},
		handleSimulateAuthorization() {
			if (!this.simulateAuthAmount || this.simulateAuthAmount <= 0 || this.processingAuthSimulation) {
				return;
			}

			this.processingAuthSimulation = true;
			const amountInCents = Math.round(this.simulateAuthAmount * 100);

			// Call API to simulate card authorization
			API.init().simulateCardAuthorization(this.userStore.currBusinessId, this.cardId, amountInCents, (response) => {
				this.processingAuthSimulation = false;

				if (response && response.result === 'OK') {
					// Success notification
					this.notificationStore.showNotification('generic-success', this.getString('issuing_simulate_authorization_success'));
					this.simulateAuthAmount = null;
					this.$emit('toggling-popup', false);

					// Trigger refresh of chart and table data after 2s timeout to wait for webhook
					const currCardId = this.cardId;
					setTimeout(() => {
						if(currCardId === this.cardId) {
							this.cardsStore.triggerRefresh();
						}
					}, 2000);
				} else {
					// Error notification
					this.notificationStore.showNotification('generic-fail', this.getString('issuing_simulate_authorization_error'));
				}
			});
		},
		handleCancelCard() {
			// Show confirmation dialog
			this.$refs.confirmPopup.toggleStatus(true);
		},
		handleCancelOtpSubmitted(otpCode) {
			this.loadingCancel = true;
			this.cardsStore.cancelCard(this.cardId, otpCode, (response) => {
				this.loadingCancel = false;
				if (response && response.result === 'OK') {
					this.notificationStore.showNotification('generic-success', this.getString('cancel_card_success'));
					this.$emit('toggling-popup', false);
				} else {
					if (response && response.details === 'invalid_otp') {
						this.notificationStore.showNotification('invalid_otp');
					} else {
						this.notificationStore.showNotification('generic-fail', this.getString('cancel_card_error'));
					}
				}
			});
		}
	},
	mounted() {
		// Spending controls data will be fetched from Stripe when the spending dropdown is opened
	},
	watch: {
		'isShowingDetails': function(newVal) {
			if (newVal) {
				const card = this.cardsStore.getCardById(this.cardId);
				if (!card || !this.cardsStore.stripe.elements) {
					console.error("Card or Stripe elements not available for card ID:", this.cardId);
					return;
				}

				// Number
				this.pinElement = this.cardsStore.stripe.elements.create('issuingCardPinDisplay', {
					issuingCard: card.stripe_id,
					nonce: this.cardsStore.stripe.nonce,
					ephemeralKeySecret: this.cardsStore.stripe.ephemeralKey,
					style: {
						base: {
							color: '#fff',
							fontSize: '18px',
							fontWeight: '500',
							textAlign: 'center',
							fontFamily: 'Montserrat, sans-serif',
						},
					},
				});
				this.pinElement.mount('#card-pin');


			} else {
				if (this.pinElement) {
					this.pinElement.unmount();
					this.pinElement = null;
				}
			}
		},
	}
})
</script>

<style scoped lang="scss">
.list-item-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	width: 100%;
	gap: 8px;

	.list-item-title {
		margin-right: 5px;
	}

	.input-wrapper {
		margin-left: auto;
		width: 130px;
	}

	.pin-shower {
		display: flex;
		align-items: center;
		gap: 10px;

		.pin-shower-value {
			min-width: 80px;
			text-align: center;
			font-size: 22px;
			font-weight: 500;
			color: var(--color-text);
			background-color: var(--color-bg-primary-4);
			border-radius: 13px;
			padding: 5px 10px;
			border: 1px solid var(--color-border);
		}
	}
}

.divider {
	width: 100%;
	height: 1px;
	background-color: var(--color-border-alpha);
	margin: 10px 0;
}

.card-spending-container {
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;

	.section-title {
		font-size: 16px;
		font-weight: 600;
		color: var(--color-txt);
		margin: 0 0 16px 0;
	}
	
	.spending-limits-section {

		.spending-limit-item {
			padding: 16px;
			background: var(--color-bg-primary-1);
			border-radius: 8px;
			border: 1px solid var(--color-border);
			z-index: 101;
			position: relative;
			
			.limit-row {
				display: flex;
				align-items: center;
				justify-content: flex-end;
				flex-wrap: wrap;
				gap: 12px;

				:deep(.app-text-input) {
					flex: 1;
				}
				
				:deep(.app-select) {
					min-width: 120px;
				}

				.select-wrapper {
					width: 180px;
				}
			}
			
			.categories-row {
				display: flex;
				align-items: center;
				gap: 8px;
				margin-top: 8px;
				
				.categories-label {
					font-size: 14px;
					color: var(--color-txt-1);
				}
				
				.categories-count {
					font-size: 14px;
					color: var(--color-txt);
					font-weight: 500;
				}
			}
		}
	}
	
	.merchant-categories-section {
		.categories-mode-selection {
			display: flex;
			border-radius: 13px;
			background-color: var(--color-bg-primary-2);
			box-shadow: inset 2px 2px 3px var(--color-box-shadow-3);
			padding: 8px;
			gap: 6px;
			margin-bottom: 16px;
			
			.mode-select-item {
				border-radius: 13px;
				border: 1px solid var(--color-primary);
				padding: 7px 12px;
				cursor: pointer;
				user-select: none;
				transition: background-color 0.3s ease;
				display: flex;
				align-items: center;
				justify-content: center;
				height: 40px;
				width: 50%;

				p {
					font-size: 15px;
					color: var(--color-primary);
					@include truncateString(1);
				}

				
				&.active {
					background-color: var(--color-active-button);
					
					p {
						color: var(--color-active-button-text);
					}
				}
			}
		}
		
		.categories-control {
			display: flex;
			align-items: center;
			justify-content: space-between;
			flex-wrap: wrap;
			gap: 12px;
			margin-bottom: 16px;
			padding: 12px;
			background: var(--color-bg-primary-1);
			border-radius: 8px;
			border: 1px solid var(--color-border);

			&:last-child {
				margin-bottom: 0;
			}
			
			.control-label {
				font-size: 14px;
				font-weight: 500;
				color: var(--color-txt);
				margin: 0;
			}
			
			.categories-summary {
				flex: 1;
				margin-left: 0;
				min-width: 150px;

				
				span {
					font-size: 14px;
					color: var(--color-txt-1);
				}
			}

			.base-button {
				margin-left: auto;
			}
		}
	}
}

.test-simulation-banner {
	background-color: var(--color-bg-primary-2);
	border: 2px dashed #FFC107;
	border-radius: 8px;
	padding: 16px;
	margin-top: 20px;
	margin-bottom: 20px;
	max-width: 600px;
	margin-left: auto;
	margin-right: auto;

	.test-warning {
		margin-bottom: 16px;
		text-align: center;

		.test-label {
			background-color: #FFC107;
			color: #000;
			padding: 4px 12px;
			border-radius: 16px;
			font-size: 12px;
			font-weight: 600;
			text-transform: uppercase;
			letter-spacing: 0.5px;
		}
	}

	.test-simulate-form {
		display: flex;
		flex-direction: row;
		flex-wrap: wrap;
		justify-content: space-between;
		gap: 12px;
		align-items: flex-end;

		.base-button {
			min-width: 140px;
			flex-shrink: 0;
		}
	}
}
</style>